import {
  getModulePath,
  LEAGUE_MODULE_NAMES,
  overwriteRoutePaths,
} from '@leagueplatform/core';
import { RouteObject } from 'react-router-dom';
import { HealthJourneyRoutes } from '@leagueplatform/health-journey';
import { AuthViewLogin, AuthViewLogout } from 'pages/auth/auth.pages';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { PreLoginLandingPage } from 'pages/pre-login-landing/pre-login-landing.page';
import { PageNotFound } from 'pages/page-not-found.page';
import { HomePage } from 'pages/home/home.page';
import { AuthSessionContainer } from 'components/auth-session-container/auth-session-container';
import { AppointmentLanding } from 'pages/appointments/appointment-landing.page';
import { AppointmentDetails } from 'pages/appointments/appointment-details.page';
import { PersonalInformation } from 'pages/personal-information/personal-information.page';
import { FAQ } from 'pages/faq/faq.page';
import { ContactUs } from 'pages/contact-us/contact-us.page';
import { PrivacyPolicy } from 'pages/privacy-policy/privacy-policy.page';
import { TOS } from 'pages/tos/tos.page';
import { InPersonConsultationPage } from 'pages/book-appointment/in-person-consultation.page';
import { VirtualConsultationPage } from 'pages/book-appointment/virtual-consultation.page';
import { LinkedCentresLandingPage } from 'pages/linked-centres/linked-centres-landing.page';
import { PATIENT_PORTAL_SYSTEM_IDS } from 'types/patient-portal-system.types';
import { TenantGuard } from 'utils/tenant-guard';

// Define League route config override
const leagueRoutesOverride = {
  [LEAGUE_MODULE_NAMES.assessment]: '/member/health-profile/questions',
  [LEAGUE_MODULE_NAMES.healthProfile]: '/member/health-profile',
  [LEAGUE_MODULE_NAMES.healthNeedPages]: '/health-need',
  [LEAGUE_MODULE_NAMES.onboarding]: '/onboarding',
  [LEAGUE_MODULE_NAMES.aboutMe]: '/member/about-me',
};

overwriteRoutePaths(leagueRoutesOverride);

const AsyncCareFinderRoutes = async () => {
  const { buildCareFinderRoutes } = await import('@leagueplatform/care-finder');
  const routes = buildCareFinderRoutes({
    results: {
      nodeId: 'careFinder$custom-results$web$customRoot',
      path: '/',
    },
  });
  return { Component: () => routes };
};

const AsyncLinkCentreRoutes = async () => {
  const { buildCareFinderRoutes } = await import('@leagueplatform/care-finder');
  const routes = buildCareFinderRoutes({
    results: {
      nodeId: 'careFinder$link-clinic$web$customRoot',
      path: '/',
    },
  });
  return { Component: () => routes };
};

export const appRoutes: RouteObject[] = [
  {
    path: APP_MANAGED_ROUTES.SIGN_IN,
    element: <AuthViewLogin />,
    children: [
      {
        path: '*',
        element: <AuthViewLogin />,
      },
    ],
  },
  {
    path: APP_MANAGED_ROUTES.SIGN_OUT,
    element: <AuthViewLogout />,
    children: [
      {
        path: '*',
        element: <AuthViewLogout />,
      },
    ],
  },
  {
    path: APP_MANAGED_ROUTES.LANDING_PAGE,
    element: <PreLoginLandingPage />,
  },
  {
    element: <AuthSessionContainer />,
    children: [
      {
        path: APP_MANAGED_ROUTES.HOME,
        element: <HomePage />,
      },
      ...[
        `${getModulePath(LEAGUE_MODULE_NAMES.healthJourney)}/*`,
        `${getModulePath(LEAGUE_MODULE_NAMES.healthPrograms)}/*`,
        `${getModulePath(LEAGUE_MODULE_NAMES.healthProgress)}/*`,
        `${getModulePath(LEAGUE_MODULE_NAMES.healthJourneyHistory)}/*`,
        `${getModulePath(LEAGUE_MODULE_NAMES.careLibrary)}/*`,
      ].map((path) => ({
        path,
        element: <HealthJourneyRoutes />, // Lazy loading is not used here due to Journey NodeRenderers required on the Homepage
      })),
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.healthProfile)}/*`,
        lazy: async () => ({
          Component: (await import('@leagueplatform/health-profile'))
            .HealthProfileRoutes,
        }),
      },
      {
        path: APP_MANAGED_ROUTES.APPOINTMENTS,
        element: <AppointmentLanding />,
      },
      {
        path: `${APP_MANAGED_ROUTES.APPOINTMENTS}/:appointmentId/:systemId`,
        element: <AppointmentDetails />,
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.careDocuments)}/*`,
        lazy: async () => {
          const { CareDocuments } = await import(
            'pages/care-documents/care-documents.component'
          );
          return { Component: CareDocuments };
        },
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.careFinder)}/*`,
        lazy: AsyncCareFinderRoutes,
      },
      {
        // can't use getModulePath here because getModulePath only works with a single instance of the module
        path: `link-centre/*`,
        lazy: AsyncLinkCentreRoutes,
      },
      {
        path: APP_MANAGED_ROUTES.IN_PERSON_CONSULTATION,
        element: <InPersonConsultationPage />,
      },
      {
        path: APP_MANAGED_ROUTES.VIRTUAL_CONSULTATION,
        element: <VirtualConsultationPage />,
      },
      {
        path: APP_MANAGED_ROUTES.PERSONAL_INFORMATION,
        element: <PersonalInformation />,
      },
      {
        path: APP_MANAGED_ROUTES.FAQ,
        element: <FAQ />,
      },
      {
        path: APP_MANAGED_ROUTES.CONTACT_US,
        element: <ContactUs />,
      },
      {
        path: APP_MANAGED_ROUTES.PRIVACY_POLICY,
        element: <PrivacyPolicy />,
      },
      {
        path: APP_MANAGED_ROUTES.TOS,
        element: <TOS />,
      },
      {
        path: APP_MANAGED_ROUTES.LINKED_CENTRES,
        element: (
          <TenantGuard allowedTenantId={PATIENT_PORTAL_SYSTEM_IDS.MY_HEALTH}>
            <LinkedCentresLandingPage />
          </TenantGuard>
        ),
      },
      {
        path: '/*',
        element: <PageNotFound />,
      },
      {
        path: '/masonry/:version/:id/*',
        lazy: async () => ({
          Component: (await import('@leagueplatform/masonry'))
            .GenericMasonryPage,
        }),
      },
    ],
  },
];
